import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { map, tap } from 'rxjs/operators';
import {
  BuildReleaseTreeParams,
  CreateReleaseParams,
  MoveReleaseAssetParams,
  Release,
  ReleaseAssetItem,
  ReleaseAssetSimple,
  ReleaseAssetTypes,
  ReleaseHistory,
  ReleaseListResponse,
  ReleaseSimple,
  ReleaseTreeState,
} from '../models/release';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { BaseTreeService } from './base-tree.service';
import { ReleaseAssetNode, ReleaseItemNode, ReleaseNode, ReleaseRootNode, TreeNodeType } from '../models/tree-node';
import { TreeSortMode } from '../../shared/models';
import { v4 as uuid4 } from 'uuid';
import moment from 'moment';
import { prepareReleaseAsset } from '../utilities/release-utilities';
import { getOptionIdFromVersionId, getVersionId } from '../utilities/subsection-utilities';
import { GeneralUtilsService } from '../../shared/services/general-utils.service';
import { EMPTY_UUID } from '../../shared/constants';

@Injectable({
  providedIn: 'root',
})
export class ReleaseTreeService extends BaseTreeService<ReleaseNode> {
  private readonly apiService = inject(ApiService);
  private readonly generalUtilsService = inject(GeneralUtilsService);
  private readonly detailLoading$ = new Subject<void>();
  private previousItemNode: ReleaseItemNode | null = null;
  private relatedOptions: Record<string, string[]> = {};

  //#region API functions

  getReleaseList(projectId: string): Observable<ReleaseListResponse> {
    const url = `/api/releasesection/project/${projectId}`;
    return this.apiService.httpGet(url).pipe(map((res: any) => res as ReleaseListResponse));
  }

  getRelease(projectId: string, releaseId: string): Observable<Release> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}`;
    return this.apiService.httpGet(url).pipe(map((res: any) => res as Release));
  }

  saveRelease(release: Release): Observable<Release> {
    const url = `/api/releasesection/project/${release.projectId}`;
    return this.apiService.httpPut(url, release).pipe(map((res: any) => res as Release));
  }

  deleteRelease(projectId: string, releaseId: string): Observable<any> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}`;
    return this.apiService.httpDelete(url);
  }

  getAssetsInRoot(projectId: string, releaseId: string): Observable<ReleaseAssetItem[]> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}/assets`;
    return this.apiService.httpGet(url).pipe(
      map((res: any) => {
        const assets = res as ReleaseAssetItem[];
        assets.forEach((asset) => {
          prepareReleaseAsset(asset);
        });
        return assets;
      })
    );
  }

  getAssetsInFolder(projectId: string, releaseId: string, folderId: string): Observable<ReleaseAssetItem[]> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}/assets/${folderId}`;
    return this.apiService.httpGet(url).pipe(
      map((res: any) => {
        const assets = res as ReleaseAssetItem[];
        assets.forEach((asset) => {
          prepareReleaseAsset(asset);
        });
        return assets;
      })
    );
  }

  getReleaseHistory(
    projectId: string,
    releaseId: string,
    pageSize: number = 10,
    pagingState: string = ''
  ): Observable<{ histories: ReleaseHistory[]; pagingState: string }> {
    let url = `/api/releasesection/project/${projectId}/section/${releaseId}/history?pageSize=${pageSize}`;
    if (pagingState) {
      url += `&pagingState=${pagingState}`;
    }
    return this.apiService
      .httpGet(url)
      .pipe(map((res: any) => res as { histories: ReleaseHistory[]; pagingState: string }));
  }

  notifyRelease(projectId: string, releaseId: string, emails: string[], message: string = ''): Observable<any> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}/notify`;
    return this.apiService.httpPost(url, { emails, message });
  }

  saveAsset(projectId: string, releaseId: string, asset: ReleaseAssetItem): Observable<ReleaseAssetItem> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}/assets`;
    return this.apiService.httpPut(url, asset).pipe(map((res: any) => res as ReleaseAssetItem));
  }

  deleteAsset(projectId: string, releaseId: string, assetId: string): Observable<any> {
    const url = `/api/releasesection/project/${projectId}/section/${releaseId}/assets/${assetId}`;
    return this.apiService.httpDelete(url);
  }

  moveReleaseAsset(params: MoveReleaseAssetParams): Observable<any> {
    const url = `/api/releasesection/project/${params.projectId}/section/${params.releaseId}/assets/${params.assetId}/move?parentId=${params.parentId}`;
    return this.apiService.httpPut(url, null);
  }

  //#endregion

  protected override state: ReleaseTreeState = {
    root: null,
    paths: {},
    selectedNode: null,
    expandedNodes: [],
    loading: false,
    releaseRecord: {},
    relatedOptions: [],
    list: [],
  };

  protected override state$ = new BehaviorSubject<ReleaseTreeState>(this.state);

  override getState(): Observable<ReleaseTreeState> {
    return this.state$.asObservable();
  }

  override updateState(update: Partial<ReleaseTreeState>) {
    this.state = {
      ...this.state,
      ...update,
    };
    this.state$.next(this.state);
  }

  override clearState() {
    this.updateState({
      root: null,
      paths: {},
      selectedNode: null,
      expandedNodes: [],
      releaseRecord: {},
      loading: false,
      list: [],
    });
  }

  buildTree(params: BuildReleaseTreeParams) {
    const { list, sortMode } = params;
    const { sections } = list;
    const record = this.populateReleaseRecord(list);
    const rootNode: ReleaseRootNode = {
      type: TreeNodeType.ReleaseRoot,
      id: uuid4(),
      name: 'Releases',
      parent: null,
      isExpandable: true,
      isExpanded: true,
      isSelected: true,
      depth: 0,
      children: [],
    };
    const simpleList: ReleaseSimple[] = sections.map((s) => ({
      id: s.id,
      title: s.title,
    }));
    simpleList.sort((a, b) => a.title.localeCompare(b.title));

    this.state = {
      root: rootNode,
      paths: {
        [rootNode.id]: [rootNode.id],
      },
      selectedNode: null,
      expandedNodes: [],
      releaseRecord: record,
      relatedOptions: Object.keys(this.relatedOptions),
      list: simpleList,
    };

    sections.forEach((item) => {
      const node: ReleaseNode = {
        type: TreeNodeType.ReleaseItem,
        id: item.id,
        name: item.title,
        parent: rootNode,
        isExpandable: true,
        isExpanded: false,
        isSelected: false,
        depth: rootNode.depth + 1,
        children: [],
        data: item,
      };
      this.addNodeToParent(rootNode, node);
    });

    this.sortTree(sortMode, true);
  }

  buildReleaseRecord(response: ReleaseListResponse) {
    const record = this.populateReleaseRecord(response);
    this.updateState({
      releaseRecord: record,
      relatedOptions: Object.keys(this.relatedOptions),
    });
  }

  populateReleaseRecord(response: ReleaseListResponse): Record<string, ReleaseAssetSimple> {
    const record: Record<string, ReleaseAssetSimple> = {};
    const map = response.map || {};
    this.relatedOptions = {};

    Object.entries(map).forEach(([assetKey, indexes]) => {
      if (!Array.isArray(indexes) || indexes.length == 0) {
        return;
      }

      const realId = assetKey.split('-').splice(1).join('-');
      //1 asset can only be part of 1 release
      const index = indexes[0];
      if (index >= response.sections.length) {
        return;
      }

      if (assetKey.startsWith('VERSION')) {
        const release = response.sections[index];
        const asset = release.assets.find(
          (a) => a.assetType == ReleaseAssetTypes.Version && getVersionId(a.assetId, a.assetVersion) == realId
        );

        if (asset) {
          if (!(asset.assetId in this.relatedOptions)) {
            this.relatedOptions[asset.assetId] = [];
          }
          if (!this.relatedOptions[asset.assetId].includes(realId)) {
            this.relatedOptions[asset.assetId].push(realId);
          }
          record[realId] = {
            id: release.id,
            title: release.title,
            assetId: asset.id,
          };
        }
      }
    });

    return record;
  }

  sortTree(sortMode: TreeSortMode, updateState: boolean = true) {
    if (updateState) {
      this.updateState({
        root: this.state.root,
      });
    }
  }

  createEmptyRelease(args: { projectId: string; companyId: string }): Observable<Release> {
    const { projectId, companyId } = args;
    const proposedName = 'Release ' + moment().format('YYYY-MM-DD');
    const newName = this.getReleaseTitle(proposedName);
    const newRelease: Release = {
      projectId,
      companyId,
      title: newName,
      assets: [],
    };

    return this.saveRelease(newRelease).pipe(
      tap((res) => {
        this.addReleaseItemNode(res);
      })
    );
  }

  createReleaseFromVersion(params: CreateReleaseParams): Observable<Release> {
    const { projectId, companyId, optionId, versionNumber, displayName } = params;
    const proposedName = displayName + ' ' + moment().format('YYYY-MM-DD');
    const newName = this.getReleaseTitle(proposedName);
    const emptyUuid = EMPTY_UUID;

    const newRelease: Release = {
      projectId,
      companyId,
      title: newName,
      assets: [
        {
          parentId: emptyUuid,
          assetType: ReleaseAssetTypes.Version,
          assetId: optionId,
          assetVersion: versionNumber,
          displayName,
        },
      ],
    };

    return this.saveRelease(newRelease).pipe(
      tap((res) => {
        this.addReleaseItemNode(res);
      })
    );
  }

  private getReleaseTitle(name: string): string {
    let counter = 0;
    let nameToCheck = name;
    let similarNameIndex = this.state.root.children.findIndex((c) => c.name == name);
    while (similarNameIndex > -1) {
      counter++;
      nameToCheck = `${name} - ${counter}`;
      similarNameIndex = this.state.root.children.findIndex((c) => c.name == nameToCheck);
    }
    return nameToCheck;
  }

  private addReleaseItemNode(release: Release): ReleaseItemNode {
    const node: ReleaseItemNode = {
      type: TreeNodeType.ReleaseItem,
      data: release,
      id: release.id,
      name: release.title,
      parent: this.state.root,
      isExpandable: true,
      isExpanded: false,
      isSelected: false,
      depth: this.state.root.depth + 1,
      children: [],
    };

    this.addNodeToParent(this.state.root, node);
    this.adjustReleaseRecord(node, false, true);
    if (!this.state.list.some((c) => c.id == release.id)) {
      const simpleInfo: ReleaseSimple = {
        id: release.id,
        title: release.title,
      };
      this.state.list.push(simpleInfo);
      this.state.list.sort((a, b) => a.title.localeCompare(b.title));
      this.updateState({
        list: this.state.list,
      });
    }
    return node;
  }

  adjustReleaseRecord(itemNode: ReleaseItemNode, removeRelated: boolean, addRelated: boolean) {
    const nodeId = itemNode.id;
    // Create a new shareRecord object
    const newRecord = { ...this.state.releaseRecord };

    //remove related
    if (removeRelated) {
      const itemIdsToDelete: string[] = [];
      Object.entries(newRecord).forEach(([itemId, release]) => {
        if (release.id == itemNode.id) {
          itemIdsToDelete.push(itemId);
        }
      });
      itemIdsToDelete.forEach((itemId) => {
        delete newRecord[itemId];
        const optionId = getOptionIdFromVersionId(itemId);
        if (optionId in this.relatedOptions && this.relatedOptions[optionId].includes(itemId)) {
          this.relatedOptions[optionId] = this.relatedOptions[optionId].filter((c) => c !== itemId);
          if (this.relatedOptions[optionId].length == 0) {
            delete this.relatedOptions[optionId];
          }
        }
      });
    }

    //add related

    if (addRelated) {
      itemNode.data.assets.forEach((asset) => {
        if (asset.assetType == ReleaseAssetTypes.Version) {
          const assetSimpleInfo = {
            id: itemNode.id,
            title: itemNode.name,
            assetId: asset.id,
          };

          const versionId = getVersionId(asset.assetId, asset.assetVersion);
          if (!newRecord[versionId]) {
            newRecord[versionId] = assetSimpleInfo;
          }

          const optionId = asset.assetId;
          if (!(optionId in this.relatedOptions)) {
            this.relatedOptions[optionId] = [];
          }
          this.relatedOptions[optionId].push(versionId);
        }
      });
    }

    this.updateState({
      releaseRecord: newRecord,
      relatedOptions: Object.keys(this.relatedOptions),
    });
  }

  private clearReleaseAssetNodes(link: ReleaseItemNode) {
    for (let i = link.children.length - 1; i >= 0; i--) {
      this.deleteNode(link.children[i] as ReleaseAssetNode);
    }
    link.children = [];
  }

  private createAssetTreeNodes(node: ReleaseItemNode) {
    const { assetTree } = node.data;
    if (!assetTree) return;

    // Process root level assets
    assetTree.forEach((item) => {
      this.buildAssetNodeRecursive({
        item,
        parent: node,
      });
    });
  }

  private buildAssetNodeRecursive(params: {
    item: ReleaseAssetItem;
    parent: ReleaseItemNode | ReleaseAssetNode;
    sortMode?: TreeSortMode;
  }): ReleaseAssetNode {
    const { item, parent, sortMode } = params;

    // Create the current asset node
    const assetNode = this.buildAssetNode({
      asset: item,
      parent,
      sortMode,
    });

    // Recursively process children if they exist
    if (item.children && item.children.length > 0) {
      item.children.forEach((childItem) => {
        this.buildAssetNodeRecursive({
          item: childItem,
          parent: assetNode,
          sortMode,
        });
      });
    }

    return assetNode;
  }

  private setPreviousItemNode(itemNode: ReleaseItemNode) {
    if (this.previousItemNode && this.previousItemNode !== itemNode) {
      this.clearReleaseAssetNodes(this.previousItemNode);
      this.previousItemNode = itemNode;
    } else if (!this.previousItemNode) {
      this.previousItemNode = itemNode;
    }
  }

  setActiveReleaseNode(itemNode: ReleaseItemNode) {
    this.setPreviousItemNode(itemNode);

    this.detailLoading$.next();
    this.updateState({
      loading: true,
    });

    if (!itemNode.children.length) {
      this.createAssetTreeNodes(itemNode);
    }

    this.setActiveNode(itemNode);
    this.getAssetsInRoot(itemNode.data.projectId, itemNode.id)
      .pipe(takeUntil(this.detailLoading$))
      .subscribe({
        next: (assets) => {
          assets.forEach((asset) => {
            const existingNode = this.findNodeById(asset.id) as ReleaseAssetNode;
            if (existingNode) {
              existingNode.data = asset;
              return;
            }
            this.buildAssetNode({
              asset,
              parent: itemNode,
            });
          });
          this.updateState({
            loading: false,
          });
        },
        error: (err) => {
          this.updateState({
            loading: false,
          });
        },
      });
  }

  setActiveReleaseById(params: { projectId: string; releaseId: string; assetId?: string; fileId?: string }) {
    const { projectId, releaseId, assetId, fileId } = params;
    const itemNode = this.findNodeById(releaseId) as ReleaseItemNode;
    if (!itemNode) {
      return;
    }

    this.setPreviousItemNode(itemNode);

    this.detailLoading$.next();
    this.updateState({
      loading: true,
    });

    if (!itemNode.children.length) {
      this.createAssetTreeNodes(itemNode);
    }

    if (!assetId) {
      this.setActiveNode(itemNode);
      this.getAssetsInRoot(projectId, releaseId)
        .pipe(takeUntil(this.detailLoading$))
        .subscribe({
          next: (assets) => {
            assets.forEach((asset) => {
              const existingNode = this.findNodeById(asset.id) as ReleaseAssetNode;
              if (existingNode) {
                existingNode.data = asset;
                return;
              }
              this.buildAssetNode({
                asset,
                parent: itemNode,
              });
            });
            this.updateState({
              loading: false,
            });
          },
          error: (err) => {
            this.updateState({
              loading: false,
            });
          },
        });
    } else {
      const assetNode = this.findNodeById(assetId) as ReleaseAssetNode;
      if (assetNode) {
        this.setActiveNode(assetNode);
        this.getAssetsInFolder(projectId, releaseId, assetId)
          .pipe(takeUntil(this.detailLoading$))
          .subscribe({
            next: (assets) => {
              assets.forEach((asset) => {
                const existingNode = this.findNodeById(asset.id) as ReleaseAssetNode;
                if (existingNode) {
                  existingNode.data = asset;
                  return;
                }

                this.buildAssetNode({
                  asset,
                  parent: assetNode,
                });
              });
              if (fileId) {
                const fileNode = this.findNodeById(fileId) as ReleaseAssetNode;
                if (fileNode) {
                  this.setActiveNode(fileNode);
                }
              }
              this.updateState({
                loading: false,
              });
            },
            error: (err) => {
              this.updateState({
                loading: false,
              });
            },
          });
      }
    }
  }

  saveAssetItem(params: {
    projectId: string;
    releaseId: string;
    asset: ReleaseAssetItem;
  }): Observable<ReleaseAssetItem> {
    const { projectId, releaseId, asset } = params;
    return this.saveAsset(projectId, releaseId, asset).pipe(
      tap((res) => {
        const parentNodeId = asset.parentId == EMPTY_UUID ? releaseId : asset.parentId;
        const parentNode = this.findNodeById(parentNodeId) as ReleaseNode;
        if (!parentNode) {
          return;
        }

        let assetNode = this.findNodeById(res.id) as ReleaseAssetNode;
        if (!assetNode) {
          assetNode = this.buildAssetNode({
            asset: res,
            parent: parentNode as ReleaseItemNode | ReleaseAssetNode,
          });
        }
        assetNode.data = res;
        const releaseNode = this.findNodeById(releaseId) as ReleaseItemNode;
        if (releaseNode) {
          if (asset.assetType == ReleaseAssetTypes.Version) {
            if (!releaseNode.data.assets.some((a) => a.id == res.id)) {
              releaseNode.data.assets.push(res);
            }
          }
          this.adjustReleaseRecord(releaseNode, true, true);
        }
      })
    );
  }

  removeAsset(params: { projectId: string; releaseId: string; assetId: string }) {
    const { projectId, releaseId, assetId } = params;

    const assetNode = this.findNodeById(assetId) as ReleaseAssetNode;
    const releaseNode = this.findNodeById(releaseId) as ReleaseItemNode;

    return this.deleteAsset(projectId, releaseId, assetId).pipe(
      tap(() => {
        if (assetNode) {
          this.deleteNode(assetNode, true);
        }
        if (releaseNode) {
          this.removeAssetFromReleaseData(releaseNode, assetId);
          this.adjustReleaseRecord(releaseNode, true, true);
        }
      })
    );
  }

  private removeAssetFromReleaseData(releaseNode: ReleaseItemNode, assetId: string) {
    releaseNode.data.assets = releaseNode.data.assets.filter((a) => a.id !== assetId);

    releaseNode.data.assetTree.forEach((item) => {
      this.removeAssetFromReleaseAssetTree(item, assetId);
    });
    releaseNode.data.assetTree = releaseNode.data.assetTree.filter((c) => c.id !== assetId);
  }

  private removeAssetFromReleaseAssetTree(asset: ReleaseAssetItem, assetId: string) {
    if (asset.children?.length) {
      asset.children.forEach((child) => {
        this.removeAssetFromReleaseAssetTree(child, assetId);
      });
    }
    asset.children = asset.children?.filter((c) => c.id !== assetId);
  }

  buildAssetNode(params: {
    asset: ReleaseAssetItem;
    parent: ReleaseItemNode | ReleaseAssetNode;
    sortMode?: TreeSortMode;
  }): ReleaseAssetNode {
    const { asset, parent, sortMode } = params;
    const assetNode: ReleaseAssetNode = {
      id: asset.id,
      name: asset.displayName,
      type: TreeNodeType.ReleaseAsset,
      assetType: asset.assetType,
      isExpandable: true,
      isSelected: false,
      isExpanded: false,
      children: [],
      parent: parent,
      depth: parent.depth + 1,
      data: asset,
    };

    this.addNodeToParent(parent, assetNode);
    return assetNode;
  }

  updateReleaseNodeById(release: Release): void {
    const itemNode = this.findNodeById(release.id) as ReleaseItemNode;
    if (!itemNode) {
      return;
    }

    // Update the node data and name
    this.updateNode(release.id, {
      data: release,
      name: release.title,
    } as Partial<ReleaseItemNode>);

    // Update the list state to reflect the new title
    const listItemIndex = this.state.list.findIndex((item) => item.id === release.id);
    if (listItemIndex !== -1) {
      this.state.list[listItemIndex] = {
        id: release.id,
        title: release.title,
      };

      // Re-sort the list alphabetically
      this.state.list.sort((a, b) => a.title.localeCompare(b.title));

      this.updateState({
        list: this.state.list,
      });
    }
  }

  deleteReleaseNodeById(id: string) {
    const node = this.findNodeById(id) as ReleaseItemNode;
    if (!node) {
      return;
    }

    this.deleteNode(node, true);
    this.adjustReleaseRecord(node, true, false);
    this.state.list = this.state.list.filter((c) => c.id !== id);
    this.updateState({
      list: this.state.list,
    });
  }
}
